/**
 * This function retrieves the 'buyPath' parameter from the URL hash.
 * The URL hash is split by ';' and the function searches for a parameter that starts with 'buyPath'.
 * If such a parameter is found, the function returns the value of the 'buyPath' parameter.
 * If no 'buyPath' parameter is found, the function returns `undefined`.
 *
 * @returns {string | undefined} The value of the 'buyPath' parameter from the URL hash, or `undefined` if no such parameter is found.
 */
export const getBuyPathFromUrl = (): string | undefined => {
  const url = new URL(window.location.href);
  const buyPathFromHash = url.hash
    .split(';')
    .find((param) => param.startsWith('buyPath'))
    ?.split('=')[1];

  if (buyPathFromHash) {
    return buyPathFromHash;
  }

  const buyPathFromUrlParams = url.pathname
    .split(';')
    .map((s) => s.trim())
    .find((s) => s.startsWith('buyPath='))
    ?.split('=')[1];

  if (buyPathFromUrlParams) {
    return buyPathFromUrlParams;
  }

  return undefined;
};

/**
 * Retrieves a parameter from the given URL by its name.
 *
 * @param {string} name - The name of the parameter to retrieve.
 * @param {string} url - The URL from which to retrieve the parameter.
 * @returns {string | null} The value of the parameter if found, null otherwise.
 */
export const getQueryParameterFromUrl = (
  name: string,
  url: string
): string | null => {
  const urlParams = new URLSearchParams(new URL(url).search);
  const param = urlParams.get(name);
  return param ? decodeURIComponent(param.replace(/\+/g, ' ')) : null;
};
